import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Send,
  AlertTriangle,
  DollarSign,
  RotateCcw,
  Gavel,
  Loader2,
  Clock,
  User,
  Shield,
  ExternalLink,
  Download,
  Sparkles,
  Percent,
  Truck,
} from 'lucide-react';
import { api } from '../../api/client';
import type { Claim } from './types';
import { AssistantPanel } from './AssistantPanel';
import { ClaimAnalysisPanel } from './ClaimAnalysisPanel';
import { BagBadge } from '../../components/BagBadge';
import { useCan } from '../../lib/rbac';

/* ── Types ─────────────────────────────────────────────── */

interface ClaimMessage {
  sender_role: string;
  receiver_role: string;
  message: string;
  translated_message: string | null;
  date_created: string;
  date_read: string | null;
  attachments: { filename: string; original_filename: string; type: string }[];
  status: string;
  stage: string;
}

interface ClaimAction {
  action: string;
  due_date: string | null;
  mandatory: boolean;
}

interface ClaimDetail {
  claim: {
    ml_claim_id: number;
    ml_order_id: number;
    status: string;
    type: string;
    stage: string;
    reason_id: string;
    detail_title: string | null;
    detail_description: string | null;
    detail_problem: string | null;
    detail_due_date: string | null;
    date_created: string;
  };
  messages: ClaimMessage[];
  actions: ClaimAction[];
  actions_history: any[];
  order: {
    ml_order_id: number;
    buyer_nickname: string;
    buyer_first_name: string;
    paid_amount: number;
    currency_id: string;
    context_site: string;
    title: string;
    seller_sku: string;
    quantity: number;
    unit_price: number;
    thumbnail: string;
  } | null;
}

interface ClaimPanelProps {
  claim: Claim;
  sellerId: number;
  onClose: () => void;
}

/* ── Helpers ───────────────────────────────────────────── */

function timeFormat(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

/** Convert HTML from ML API messages to plain text with line breaks */
function stripHtml(html: string): string {
  if (!html) return '';
  if (!/<[a-z][\s\S]*>|&[#a-z0-9]+;/i.test(html)) return html;
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<\/?(p|div|li|tr)[^>]*>/gi, '\n');
  // DOMParser decodifica TODAS las entidades (&oacute;, &iexcl;, &ntilde;...) y quita las etiquetas restantes
  const text = new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent || '';
  return text.replace(/ /g, ' ').replace(/\n{3,}/g, '\n\n').trim();
}

function formatCurrency(amount: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
}

const ACTION_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; confirm: string; cap: string }> = {
  refund: {
    cap: 'orders.refund',
    label: 'Full Refund',
    icon: <DollarSign size={14} />,
    color: 'var(--color-error)',
    confirm: 'Are you sure you want to issue a FULL REFUND? This cannot be undone.',
  },
  allow_return: {
    cap: 'orders.refund',
    label: 'Accept Return',
    icon: <RotateCcw size={14} />,
    color: 'var(--color-warning)',
    confirm: 'Accept the return? The buyer will ship the product back.',
  },
  open_dispute: {
    cap: 'orders.refund',
    label: 'Escalate to ML',
    icon: <Gavel size={14} />,
    color: 'var(--color-info)',
    confirm: 'Escalate this claim to a Mercado Libre mediator?',
  },
  // ML lo habilita según el tipo de claim (viene en respondent_actions).
  // No usa `confirm`: abre modal propio con las offers en vivo.
  allow_partial_refund: {
    cap: 'orders.refund',
    label: 'Partial Refund',
    icon: <Percent size={14} />,
    color: '#8b5cf6',
    confirm: '',
  },
};

const ROLE_ICONS: Record<string, React.ReactNode> = {
  complainant: <User size={14} />,
  respondent: <Shield size={14} />,
  mediator: <Gavel size={14} />,
};

/* ── Component ─────────────────────────────────────────── */

export function ClaimPanel({ claim, sellerId, onClose }: ClaimPanelProps) {
  const { t } = useTranslation();
  const can = useCan();
  const [detail, setDetail] = useState<ClaimDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [assistantOpen, setAssistantOpen] = useState(false);
  // Modal Accept Return: costo de envío de la devolución que ML nos descuenta
  const [returnModal, setReturnModal] = useState<
    | null
    | {
        loading: boolean;
        cost: { currency_id: string; amount: number; amount_usd?: number } | null;
        error: boolean;
      }
  >(null);
  // Modal Partial Refund: porcentajes habilitados por ML con monto por opción
  const [partialModal, setPartialModal] = useState<
    | null
    | {
        loading: boolean;
        currency: string;
        offers: { amount: number; percentage: number }[];
        selected: number | null;
        error: string | null;
      }
  >(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ClaimDetail>(`/v3/claims/${claim.ml_claim_id}?seller_id=${sellerId}`);
      if (res.success && res.data) {
        setDetail(res.data);
      } else {
        setError(res.error || 'Failed to load claim');
      }
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [claim.ml_claim_id, sellerId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  useEffect(() => {
    if (detail?.messages) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [detail?.messages]);

  const handleSendMessage = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/v3/claims/${claim.ml_claim_id}/send-message`, {
        seller_id: sellerId,
        message: message.trim(),
        receiver_role: messageReceiver,
      }) as any;
      if (res.success) {
        setMessage('');
        await fetchDetail();
      } else {
        alert(`Error: ${res.error || 'Failed to send'}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  // Accept Return: consulta el costo real que ML nos descuenta y lo muestra
  // en el modal ANTES de confirmar (igual que el popup de ML web).
  const openReturnModal = async () => {
    setReturnModal({ loading: true, cost: null, error: false });
    try {
      const res = (await api.get(
        `/mercadolibre/claims/${claim.ml_claim_id}/return-cost?seller_id=${sellerId}`
      )) as any;
      if (res.success && res.data && typeof res.data.amount === 'number') {
        setReturnModal({ loading: false, cost: res.data, error: false });
      } else {
        setReturnModal({ loading: false, cost: null, error: true });
      }
    } catch {
      // Sin costo no bloqueamos la acción: el modal avisa y deja continuar.
      setReturnModal({ loading: false, cost: null, error: true });
    }
  };

  const confirmReturn = async () => {
    setActionLoading('allow_return');
    try {
      const res = (await api.post(`/v3/claims/${claim.ml_claim_id}/allow-return`, {
        seller_id: sellerId,
      })) as any;
      if (res.success) {
        setReturnModal(null);
        await fetchDetail();
      } else {
        alert(`Error: ${res.error || 'Action failed'}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading('');
    }
  };

  // Partial Refund: trae los porcentajes que ML habilita para ESTE claim
  // (con monto por opción) y manda la oferta elegida.
  const openPartialModal = async () => {
    setPartialModal({ loading: true, currency: 'USD', offers: [], selected: null, error: null });
    try {
      const res = (await api.get(
        `/mercadolibre/claims/${claim.ml_claim_id}/partial-refund-offers?seller_id=${sellerId}`
      )) as any;
      const offers = (res?.data?.available_offers || [])
        .slice()
        .sort((a: any, b: any) => b.percentage - a.percentage);
      if (res.success && offers.length) {
        setPartialModal({
          loading: false,
          currency: res.data.currency_id || 'USD',
          offers,
          selected: null,
          error: null,
        });
      } else {
        setPartialModal({
          loading: false,
          currency: 'USD',
          offers: [],
          selected: null,
          error: 'ML does not offer partial refund options for this claim right now.',
        });
      }
    } catch (err: any) {
      setPartialModal({
        loading: false,
        currency: 'USD',
        offers: [],
        selected: null,
        error: err?.message || 'Failed to load offers',
      });
    }
  };

  const sendPartialOffer = async () => {
    if (!partialModal?.selected) return;
    setActionLoading('allow_partial_refund');
    try {
      const res = (await api.post(
        `/mercadolibre/claims/${claim.ml_claim_id}/partial-refund?seller_id=${sellerId}`,
        { percentage: partialModal.selected }
      )) as any;
      if (res.success) {
        setPartialModal(null);
        await fetchDetail();
      } else {
        alert(`Error: ${res.error || 'Failed to send offer'}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading('');
    }
  };

  const handleAction = async (actionKey: string) => {
    const config = ACTION_CONFIG[actionKey];
    if (!config) return;
    // Estas dos acciones abren modal propio (costo / offers en vivo de ML)
    if (actionKey === 'allow_return') {
      openReturnModal();
      return;
    }
    if (actionKey === 'allow_partial_refund') {
      openPartialModal();
      return;
    }
    if (!window.confirm(config.confirm)) return;

    setActionLoading(actionKey);
    try {
      const endpoint = actionKey === 'allow_return' ? 'allow-return' : actionKey === 'open_dispute' ? 'open-dispute' : actionKey;
      const res = await api.post(`/v3/claims/${claim.ml_claim_id}/${endpoint}`, {
        seller_id: sellerId,
      }) as any;
      if (res.success) {
        await fetchDetail();
      } else {
        alert(`Error: ${res.error || 'Action failed'}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading('');
    }
  };

  const handleSendInvoice = async () => {
    if (invoiceSending || invoiceStatus === 'sent') return;
    setInvoiceSending(true);
    try {
      const res = await api.post(`/mercadolibre/claims/${claim.ml_claim_id}/send-invoice?seller_id=${sellerId}`, {}) as any;
      setInvoiceStatus(res.success ? 'sent' : 'error');
    } catch {
      setInvoiceStatus('error');
    } finally {
      setInvoiceSending(false);
    }
  };

  const messageAction = detail?.actions?.find(
    a => a.action === 'send_message_to_complainant' || a.action === 'send_message_to_mediator'
  );
  // Escribir a mano en el reclamo exige la llave de mensajes.
  const canSendMessage = !!messageAction && can('orders.messages');
  // Al asistente del reclamo se llega con la llave de mensajes O con la de reclamos.
  const canWriteClaim = !!messageAction && (can('orders.messages') || can('orders.claims_manage'));
  const messageReceiver: 'complainant' | 'mediator' =
    messageAction?.action === 'send_message_to_mediator' ? 'mediator' : 'complainant';
  const availableActions = detail?.actions?.filter(
    a => a.action !== 'send_message_to_complainant' && a.action !== 'send_message_to_mediator'
  ) || [];
  const isOpen = detail?.claim?.status === 'opened';
  const isMediation = detail?.claim?.stage === 'dispute' || messageReceiver === 'mediator';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-[var(--color-bg)] h-full flex flex-col shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[var(--color-bg)] border-b border-[var(--color-border)] px-4 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle size={16} className="text-[var(--color-error)] flex-shrink-0" />
            <span className="font-bold text-[var(--color-text)] truncate">
              Claim #{claim.ml_claim_id}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              isOpen
                ? 'bg-[var(--color-error-light)] text-[var(--color-error)]'
                : 'bg-[var(--color-bg)] text-[var(--color-text-muted)]'
            }`}>
              {detail?.claim?.status || claim.status}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`https://www.mercadolibre.com/ventas/${detail?.claim?.ml_order_id || ''}/detalle`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]"
              title="View on ML"
            >
              <ExternalLink size={16} />
            </a>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]">
              <X size={18} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 size={24} className="animate-spin text-[var(--color-primary)]" />
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center text-[var(--color-error)] text-sm p-4">
            {error}
          </div>
        ) : detail ? (
          <>
            {/* Zona scrolleable única: info del claim + mensajes. Antes la info era
                fija y en ventanas bajas empujaba el input del asistente fuera del
                viewport (bug "tapado por la barra de Windows", 2026-07-02) */}
            <div className="flex-1 min-h-0 overflow-y-auto">
            {/* Bolsa WH — bloque destacado al inicio */}
            {claim.ml_order_id && (
              <div className="px-4 pt-3">
                <BagBadge mlOrderId={claim.ml_order_id} sellerId={sellerId} variant="full" />
              </div>
            )}

            {/* Claim info + product */}
            <div className="px-4 py-3 border-b border-[var(--color-border)] space-y-2">
              {/* Title + problem */}
              {detail.claim.detail_title && (
                <p className="text-sm font-medium text-[var(--color-text)]">{detail.claim.detail_title}</p>
              )}
              {detail.claim.detail_problem && (
                <p className="text-xs text-[var(--color-text-secondary)]">{detail.claim.detail_problem}</p>
              )}
              {detail.claim.detail_due_date && isOpen && (
                <p className="text-xs text-[var(--color-warning)] font-medium flex items-center gap-1">
                  <Clock size={12} />
                  Deadline: {new Date(detail.claim.detail_due_date).toLocaleDateString('en-US', {
                    weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              )}

              {/* Product card */}
              {detail.order && (
                <div className="flex items-center gap-3 p-2 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)]">
                  {detail.order.thumbnail && (
                    <img src={detail.order.thumbnail} alt="" className="w-10 h-10 rounded object-cover" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-[var(--color-text)] truncate">{detail.order.title}</p>
                    <p className="text-[10px] text-[var(--color-text-muted)]">
                      {detail.order.seller_sku} · x{detail.order.quantity}{can('global.view_money') ? ` · ${formatCurrency(detail.order.paid_amount, detail.order.currency_id)}` : ''}
                      {' · '}{detail.order.buyer_nickname}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Messages — chat style, oldest first (chronological) */}
            <div className="px-4 py-3 space-y-3">
              {detail.messages.length === 0 ? (
                <p className="text-xs text-[var(--color-text-muted)] text-center py-8">No messages yet</p>
              ) : (
                [...detail.messages].reverse().map((msg, idx) => {
                  const isUs = msg.sender_role === 'respondent';
                  const isMediator = msg.sender_role === 'mediator';
                  return (
                    <div key={idx} className={`flex ${isUs ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-xl px-3 py-2 text-xs space-y-1 ${
                        isMediator
                          ? 'bg-[var(--color-warning-light)] border border-[var(--color-warning)]'
                          : isUs
                            ? 'bg-[var(--color-primary)] text-white'
                            : 'bg-[var(--color-surface)] border border-[var(--color-border)]'
                      }`}>
                        {/* Sender label */}
                        <div className={`flex items-center gap-1 text-[10px] font-medium ${
                          isUs ? 'text-white/70' : 'text-[var(--color-text-muted)]'
                        }`}>
                          {ROLE_ICONS[msg.sender_role] || <User size={12} />}
                          {msg.sender_role === 'complainant' ? 'Buyer' : msg.sender_role === 'respondent' ? 'You' : 'ML Mediator'}
                        </div>
                        {/* Message text */}
                        <div className={`whitespace-pre-wrap ${isUs ? 'text-white' : 'text-[var(--color-text)]'}`}>
                          {stripHtml(msg.message)}
                        </div>
                        {/* Translation */}
                        {msg.translated_message && msg.translated_message !== msg.message && (
                          <div className={`whitespace-pre-wrap italic ${isUs ? 'text-white/60' : 'text-[var(--color-text-muted)]'}`}>
                            {stripHtml(msg.translated_message)}
                          </div>
                        )}
                        {/* Attachments — HD preview for images, download link for all */}
                        {msg.attachments?.length > 0 && (
                          <div className="flex flex-wrap gap-2 pt-1">
                            {msg.attachments.map((att, ai) => {
                              const isImage = att.type?.startsWith('image/');
                              const proxyUrl = `/api/v3/claims/${detail.claim.ml_claim_id}/attachments/${encodeURIComponent(att.filename)}?seller_id=${sellerId}`;
                              return isImage ? (
                                <a key={ai} href={proxyUrl} target="_blank" rel="noopener noreferrer"
                                   className="block max-w-[260px] rounded-lg overflow-hidden border border-white/20 hover:border-white/50 transition-colors">
                                  <img src={proxyUrl} alt={att.original_filename || att.filename}
                                       className="w-full h-auto max-h-[300px] object-contain bg-black/10" loading="lazy" />
                                  <div className={`flex items-center gap-1.5 px-2 py-1 text-[10px] ${
                                    isUs ? 'text-white/70' : 'text-[var(--color-text-muted)]'
                                  }`}>
                                    <Download className="w-3 h-3 shrink-0" />
                                    <span className="truncate">{att.original_filename || att.filename}</span>
                                  </div>
                                </a>
                              ) : (
                                <a key={ai} href={proxyUrl} target="_blank" rel="noopener noreferrer"
                                   className={`flex items-center gap-1.5 text-[10px] px-2 py-1 rounded ${
                                     isUs ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-[var(--color-bg)] hover:bg-[var(--color-surface-hover)]'
                                   } transition-colors`}>
                                  <Download className="w-3 h-3 shrink-0" />
                                  {att.original_filename || att.filename}
                                </a>
                              );
                            })}
                          </div>
                        )}
                        {/* Time + read status */}
                        <div className={`flex items-center gap-1.5 text-[10px] ${isUs ? 'text-white/50' : 'text-[var(--color-text-muted)]'}`}>
                          <span>{timeFormat(msg.date_created)}</span>
                          {isUs && (
                            msg.date_read
                              ? <span className={isUs ? 'text-white/90' : 'text-blue-500'} title={`Leído: ${timeFormat(msg.date_read)}`}>✓✓</span>
                              : <span className={isUs ? 'text-white/40' : 'text-[var(--color-text-muted)]'}>✓</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
            </div>{/* fin zona scrolleable única */}

            {/* Actions bar */}
            {isOpen && availableActions.length > 0 && (
              <div className="px-4 py-2 border-t border-[var(--color-border)] flex items-center gap-2 flex-wrap">
                {availableActions.map(a => {
                  const config = ACTION_CONFIG[a.action];
                  // Sin la llave de reembolsos, ninguno de estos botones se ofrece.
                  if (!config || !can(config.cap)) return null;
                  const isLoading = actionLoading === a.action;
                  return (
                    <button
                      key={a.action}
                      onClick={() => handleAction(a.action)}
                      disabled={!!actionLoading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
                      style={{ backgroundColor: config.color }}
                    >
                      {isLoading ? <Loader2 size={14} className="animate-spin" /> : config.icon}
                      {config.label}
                      {a.mandatory && <span className="text-[9px] opacity-70">(required)</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Ficha del asistente nuevo (etapa 2): arriba del agente viejo, que sigue igual.
                Sus datos salen de rutas del asistente: exige además la llave del asistente. */}
            {isOpen && canWriteClaim && can('assistant.chat') && (
              <ClaimAnalysisPanel mlClaimId={claim.ml_claim_id} sellerId={sellerId} />
            )}

            {/* Toggle Manual / Asistente AI */}
            {isOpen && canWriteClaim && (
              <div className="px-4 pt-2 -mb-1 border-t border-[var(--color-border)] flex items-center gap-2">
                {canSendMessage && (
                <button
                  onClick={() => setAssistantOpen(false)}
                  className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded transition-colors ${
                    !assistantOpen
                      ? 'bg-[var(--color-primary)] text-white'
                      : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-hover)]'
                  }`}
                >
                  <Send size={11} /> Manual
                </button>
                )}
                <button
                  onClick={() => setAssistantOpen(true)}
                  className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded transition-colors ${
                    assistantOpen || !canSendMessage
                      ? 'bg-purple-600 text-white'
                      : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-hover)]'
                  }`}
                >
                  <Sparkles size={11} /> Asistente AI
                </button>
              </div>
            )}

            {/* Asistente AI sub-panel — sin la llave de mensajes es la única vía, así que queda abierto */}
            {isOpen && canWriteClaim && (assistantOpen || !canSendMessage) && (
              /* basis 60vh pero CON shrink: en ventanas bajas se achica (piso 280px)
                 para que el input del asistente nunca quede fuera del viewport */
              <div className="border-t border-[var(--color-border)] basis-[60vh] min-h-[280px]">
                <AssistantPanel
                  mlClaimId={claim.ml_claim_id}
                  sellerId={sellerId}
                  onClose={() => setAssistantOpen(false)}
                />
              </div>
            )}

            {/* Message input */}
            {isOpen && canSendMessage && !assistantOpen && (
              <div className="px-4 py-3 border-t border-[var(--color-border)]">
                {isMediation && (
                  <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-[var(--color-info)]">
                    <Gavel size={12} />
                    Conversación con el mediador de Mercado Libre
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      isMediation
                        ? t('orders.claims.messagePlaceholderMediator', 'Escribí un mensaje al mediador de ML…')
                        : t('orders.claims.messagePlaceholder', 'Type a message to the buyer...')
                    }
                    className="flex-1 text-sm p-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] resize-none outline-none focus:border-[var(--color-primary)] transition-colors"
                    rows={2}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!message.trim() || sending}
                    className="h-10 w-10 flex items-center justify-center rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity flex-shrink-0"
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </div>
                <div className="flex items-center mt-2">
                  <button
                    onClick={handleSendInvoice}
                    disabled={invoiceSending || invoiceStatus === 'sent'}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      invoiceStatus === 'sent'
                        ? 'bg-green-600 text-white'
                        : invoiceStatus === 'error'
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-[var(--color-surface)] text-[var(--color-text)] border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]'
                    }`}
                  >
                    {invoiceSending ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />}
                    {invoiceSending ? 'Enviando...' : invoiceStatus === 'sent' ? 'Invoice Enviado' : invoiceStatus === 'error' ? 'Error — Reintentar' : 'Send Invoice'}
                  </button>
                </div>
              </div>
            )}

            {/* Closed state */}
            {!isOpen && (
              <div className="px-4 py-3 border-t border-[var(--color-border)] text-center text-xs text-[var(--color-text-muted)]">
                This claim is <strong>{detail.claim.status}</strong>
              </div>
            )}
          </>
        ) : null}

        {/* Modal Accept Return — muestra el costo que ML nos descuenta ANTES de confirmar */}
        {returnModal && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setReturnModal(null)}
          >
            <div
              className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] shadow-xl w-full max-w-sm p-4 space-y-3"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-[var(--color-text)] flex items-center gap-2">
                  <RotateCcw size={15} className="text-[var(--color-warning)]" />
                  You're about to enable a return
                </h3>
                <button
                  onClick={() => setReturnModal(null)}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                >
                  <X size={16} />
                </button>
              </div>
              {returnModal.loading ? (
                <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] py-3">
                  <Loader2 size={14} className="animate-spin" /> Checking return shipping cost…
                </div>
              ) : returnModal.cost ? (
                <div className="text-xs text-[var(--color-text-secondary)] space-y-2">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)]">
                    <Truck size={16} className="text-[var(--color-warning)] shrink-0" />
                    <span>
                      ML will deduct{' '}
                      <strong className="text-[var(--color-text)]">
                        {can('global.view_money')
                          ? formatCurrency(
                              returnModal.cost.amount_usd ?? returnModal.cost.amount,
                              returnModal.cost.amount_usd ? 'USD' : returnModal.cost.currency_id
                            )
                          : '—'}
                      </strong>{' '}
                      for return shipping
                      {can('global.view_money') &&
                        returnModal.cost.amount_usd &&
                        returnModal.cost.currency_id !== 'USD' && (
                          <span className="text-[var(--color-text-muted)]">
                            {' '}({formatCurrency(returnModal.cost.amount, returnModal.cost.currency_id)})
                          </span>
                        )}
                      .
                    </span>
                  </div>
                  <p>
                    The selling price will be refunded to the buyer once the product has been
                    dispatched.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-[var(--color-warning)]">
                  Couldn't fetch the return shipping cost from ML. You can still confirm the
                  return — ML will deduct the standard return shipping charge.
                </p>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => setReturnModal(null)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReturn}
                  disabled={returnModal.loading || actionLoading === 'allow_return'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-white hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: 'var(--color-warning)' }}
                >
                  {actionLoading === 'allow_return' ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <RotateCcw size={13} />
                  )}
                  Confirm return
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Partial Refund — porcentajes habilitados por ML con monto por opción */}
        {partialModal && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setPartialModal(null)}
          >
            <div
              className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] shadow-xl w-full max-w-sm p-4 space-y-3"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-[var(--color-text)] flex items-center gap-2">
                  <Percent size={15} style={{ color: '#8b5cf6' }} />
                  Offer a refund percentage
                </h3>
                <button
                  onClick={() => setPartialModal(null)}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                >
                  <X size={16} />
                </button>
              </div>
              <p className="text-xs text-[var(--color-text-secondary)]">
                Once the buyer accepts the offer, this claim will be closed and will no longer
                affect your reputation.
              </p>
              {partialModal.loading ? (
                <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] py-3">
                  <Loader2 size={14} className="animate-spin" /> Loading refund options…
                </div>
              ) : partialModal.error ? (
                <p className="text-xs text-[var(--color-warning)]">{partialModal.error}</p>
              ) : (
                <>
                  {partialModal.offers.length > 0 && can('global.view_money') && (
                    <div className="text-xs text-[var(--color-text-secondary)]">
                      Product price:{' '}
                      <strong className="text-[var(--color-text)]">
                        {formatCurrency(
                          (partialModal.offers[0].amount / partialModal.offers[0].percentage) * 100,
                          partialModal.currency
                        )}
                      </strong>
                    </div>
                  )}
                  <div className="max-h-56 overflow-y-auto grid grid-cols-3 gap-1.5">
                    {partialModal.offers.map(o => {
                      const isSel = partialModal.selected === o.percentage;
                      return (
                        <button
                          key={o.percentage}
                          onClick={() =>
                            setPartialModal({ ...partialModal, selected: o.percentage })
                          }
                          className={`px-2 py-2 rounded-lg border text-center transition-colors ${
                            isSel
                              ? 'border-[#8b5cf6] bg-[#8b5cf6]/10'
                              : 'border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]'
                          }`}
                        >
                          <div className="text-sm font-bold text-[var(--color-text)]">
                            {o.percentage}%
                          </div>
                          <div className="text-[10px] text-[var(--color-text-secondary)]">
                            {can('global.view_money') ? formatCurrency(o.amount, partialModal.currency) : '—'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => setPartialModal(null)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
                >
                  Cancel
                </button>
                <button
                  onClick={sendPartialOffer}
                  disabled={
                    partialModal.loading ||
                    !partialModal.selected ||
                    actionLoading === 'allow_partial_refund'
                  }
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-white hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: '#8b5cf6' }}
                >
                  {actionLoading === 'allow_partial_refund' ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Send size={13} />
                  )}
                  Send offer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
